import type { CryptoWithdrawal, FinpspTransactionReport, WithdrawalStatus } from '../@types/api';
import { type ApprovalActionConfig, getActionsForStatus } from '../constants/approvalActions';
import { FINPSP_STATE } from '../constants/finpsp';
import { APPROVAL_ACTION, PAYMENT_GATEWAY } from '../constants/enums';

/** Normalized withdrawal provider identity; anything unrecognised or contradictory is `invalid`. */
export type WithdrawalProvider = 'legacy' | 'zero_x' | 'finpsp' | 'invalid';

export type PayoutCapabilities = { approve: boolean; reject: boolean };

type WithdrawalRow = {
  status: WithdrawalStatus;
  paymentMethod?: string;
  paymentProvider?: string | null;
  cryptoWithdrawal?: CryptoWithdrawal;
  finpsp?: FinpspTransactionReport;
};

const LEGACY_PROVIDERS = new Set(['LIGDICASH', 'PAY2LOCAL', 'KAPOPAY', 'CRYPTO']);
const ZERO_X_PROVIDER = 'ZERO_X_PROCESSING';

export const normalizeWithdrawalProvider = ({
  paymentMethod,
  paymentProvider,
  cryptoWithdrawal,
  finpsp,
}: WithdrawalRow): WithdrawalProvider => {
  const provider = paymentProvider?.trim().toUpperCase() || null;
  const zeroXFacts = cryptoWithdrawal?.provider === 'zero_x_processing';

  if (provider === PAYMENT_GATEWAY.FINPSP)
    return finpsp?.gateway === PAYMENT_GATEWAY.FINPSP && !cryptoWithdrawal ? 'finpsp' : 'invalid';
  if (finpsp) return 'invalid';

  if (provider === ZERO_X_PROVIDER) return zeroXFacts ? 'zero_x' : 'invalid';
  if (cryptoWithdrawal) return 'invalid';
  // Missing provider facts must not turn a submitted crypto payout into a manual withdrawal.
  if (provider === null && paymentMethod?.trim().toUpperCase() === 'CRYPTO') return 'invalid';
  if (provider === null || LEGACY_PROVIDERS.has(provider)) return 'legacy';
  return 'invalid';
};

/** The complete `(provider, status, provider state) → actions` decision. Components only render it. */
export const getWithdrawalActions = (row: WithdrawalRow, capabilities: PayoutCapabilities): ApprovalActionConfig[] => {
  const provider = normalizeWithdrawalProvider(row);

  switch (provider) {
    case 'legacy':
      return getActionsForStatus(row.status);
    case 'zero_x':
      // Once submitted, only verified provider evidence may settle or release a 0x payout.
      return row.status === 'pending' && row.cryptoWithdrawal?.state === 'awaiting_approval'
        ? getActionsForStatus('pending')
        : [];
    case 'finpsp':
      return row.status === 'pending' && row.finpsp?.state === FINPSP_STATE.AWAITING_APPROVAL
        ? getActionsForStatus('pending')
            .filter((action) =>
              action.action === APPROVAL_ACTION.APPROVE
                ? capabilities.approve
                : action.action === APPROVAL_ACTION.REJECT && capabilities.reject
            )
            .map((action) =>
              action.action === APPROVAL_ACTION.APPROVE
                ? { ...action, successMessage: 'Withdrawal submitted for processing.' }
                : action
            )
        : [];
    case 'invalid':
      return [];
  }
};
